/**
 * ArtifactRail — the detail page's right rail. Demo page: /file-manager item details (facts card) and /profile rail.
 * Details and references; links go to the referenced entity.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../../components/ui/index.js';
import { MEMORY_SCOPE_LABEL, subjectLabel } from '../memory-detail/memory-scope.js';
import { formatDate, getKindConfig, type ArtifactKind, type ArtifactScope } from './kind-config.js';

interface ArtifactRailProps {
  kind: ArtifactKind;
  scope: ArtifactScope;
  subjectType: string;
  version: number;
  createdAt: string;
  updatedAt: string;
  references: readonly string[];
}

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-border-primary py-3 first:pt-0 last:border-0 last:pb-0">
    <dt className="text-sm text-muted">{label}</dt>
    <dd className="text-right text-sm text-heading">{children}</dd>
  </div>
);

/** A reference string names an entity by its id prefix; there is no resolver endpoint, so unknown ones are plain text. */
function resolveReference(ref: string): { type: string; path: string } | null {
  const lower = ref.toLowerCase();
  if (lower.startsWith('conv')) return { type: 'Conversation', path: `/conversations/${ref}` };
  if (lower.startsWith('task')) return { type: 'Task', path: `/tasks/${ref}` };
  if (lower.startsWith('art') || /^[0-9a-f-]{36}$/i.test(ref)) return { type: 'Artifact', path: `/artifacts/${ref}` };
  return null;
}

export const ArtifactRail: React.FC<ArtifactRailProps> = ({
  kind,
  scope,
  subjectType,
  version,
  createdAt,
  updatedAt,
  references,
}) => {
  const counts = new Map<string, number>();
  const labelled = references.map((ref) => {
    const resolved = resolveReference(ref);
    const type = resolved?.type ?? 'Reference';
    const n = (counts.get(type) ?? 0) + 1;
    counts.set(type, n);
    return { ref, path: resolved?.path ?? null, type, n };
  });

  return (
    <>
      <Card title="Details">
        <dl>
          <Fact label="Type">{getKindConfig(kind).badgeLabel}</Fact>
          <Fact label="Scope">{MEMORY_SCOPE_LABEL[scope]}</Fact>
          <Fact label="About">{subjectLabel(subjectType)}</Fact>
          <Fact label="Version">v{version}</Fact>
          <Fact label="Created">{formatDate(createdAt)}</Fact>
          <Fact label="Updated">{formatDate(updatedAt)}</Fact>
        </dl>
      </Card>
      <Card title="References">
        {labelled.length === 0 ? (
          <p className="text-sm text-muted">This artifact does not reference anything.</p>
        ) : (
          <ul className="space-y-2">
            {labelled.map(({ ref, path, type, n }) => {
              const text = (counts.get(type) ?? 1) > 1 ? `${type} ${n}` : type;
              return (
                <li key={ref} className="text-sm">
                  {path ? (
                    <Link to={path} className="text-accent-text hover:underline">
                      {text}
                    </Link>
                  ) : (
                    <span className="text-muted">{text}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </>
  );
};
